import { ReactNode, useState } from 'react';
import { ChevronDown, LucideIcon } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';

// Shared shell for the KPI cards + section cards every rebuilt role dashboard
// uses. One chrome, one header pattern, one padding rhythm — the figures are
// what differ.

/**
 * The number is the hero: mono, tabular, India-grouped (callers pass text
 * already run through formatINR/formatMoney) and set large (22px) so it reads
 * before anything else on the card. `hex` tints the icon chip — a soft fill of
 * the same hue behind the icon — never the figure.
 */
export function KpiCard({ label, value, icon: Icon, hex }: { label: string; value: string; icon?: LucideIcon; hex?: string }) {
  return (
    <Card className="group flex h-full flex-col justify-between gap-3 bg-card p-4 shadow-surface-1 transition-shadow hover:shadow-surface-2">
      <div className="flex items-start justify-between gap-2">
        <span className="text-body-sm font-medium leading-snug text-on-surface-variant">{label}</span>
        {Icon ? (
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-container text-primary-dim"
            style={hex ? { color: hex, backgroundColor: `${hex}1A` } : undefined}
            aria-hidden
          >
            <Icon className="h-4 w-4" />
          </span>
        ) : null}
      </div>
      <div className="truncate font-mono text-[22px] font-semibold leading-tight tabular-nums text-on-surface" title={value}>{value}</div>
    </Card>
  );
}

// Tailwind's JIT scans source for literal class strings — `lg:col-span-${n}`
// never matches anything, so every span callers use is spelled out here.
const SPAN_CLASS: Record<number, string> = {
  3: 'col-span-12 lg:col-span-3',
  4: 'col-span-12 lg:col-span-4',
  5: 'col-span-12 lg:col-span-5',
  6: 'col-span-12 lg:col-span-6',
  7: 'col-span-12 lg:col-span-7',
  8: 'col-span-12 lg:col-span-8',
  9: 'col-span-12 lg:col-span-9',
  12: 'col-span-12',
};

export function SectionCard({
  icon: Icon, title, subtitle, span, action, children,
}: { icon?: LucideIcon; title: string; subtitle?: string; span: number; action?: ReactNode; children: ReactNode }) {
  return (
    <Card className={`${SPAN_CLASS[span] || SPAN_CLASS[12]} flex h-full flex-col overflow-hidden bg-card shadow-surface-1`}>
      <div className="flex items-center gap-3 border-b border-border bg-surface-container-low/60 px-4 py-3">
        {Icon ? (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-container text-primary-dim" aria-hidden>
            <Icon className="h-4 w-4" />
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-body-md font-semibold text-on-surface">{title}</h3>
          {/* The subtitle earns its place only when it says what the figures cover. */}
          {subtitle ? <p className="truncate text-data-xs text-on-surface-subtle">{subtitle}</p> : null}
        </div>
        {action}
      </div>
      <div className="flex-1 p-4">{children}</div>
    </Card>
  );
}

export interface SimpleListItem {
  icon: LucideIcon;
  title: string;
  meta: string;
  tag: string;
  /** Tints the status dot only; the tag text stays on the accessible neutral. */
  tagHex?: string;
  /** Right-aligned figure column — mono and tabular, like every other number here. */
  value?: string;
}

export function SimpleList({ items }: { items: SimpleListItem[] }) {
  if (items.length === 0) {
    return (
      <p className="py-6 text-center text-body-sm text-on-surface-variant">
        Nothing matches this view yet. Widen the period or raise the record it reads from.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-border">
      {items.map((it, i) => (
        <li key={i} className="flex items-center gap-3 px-1 py-2.5">
          <span
            className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-surface-dim"
            style={it.tagHex ? { backgroundColor: it.tagHex } : undefined}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-data-md font-medium text-on-surface">{it.title}</div>
            <div className="truncate text-body-sm text-on-surface-variant">{it.meta}</div>
          </div>
          {it.value ? (
            <span className="shrink-0 font-mono text-data-md tabular-nums text-on-surface">{it.value}</span>
          ) : null}
          <span className="shrink-0 whitespace-nowrap rounded-lg bg-surface-variant px-2 py-0.5 text-data-xs font-medium text-on-surface-variant">
            {it.tag}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The hairline totals row every ledger table ends with: rule above, figure in
 *  mono, label in uppercase caps. */
export function TotalsRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-1 pt-2.5">
      <span className="text-label-sm uppercase tracking-wider text-on-surface-variant">{label}</span>
      <span className="font-mono text-data-md font-semibold tabular-nums text-on-surface">{value}</span>
    </div>
  );
}

/**
 * A long list (attendance of the whole company) opened at its first rows, with
 * the rest one click away — 35 rows of "Not in" pushed everything else on the
 * dashboard off the screen (2026-10-23).
 */
export function useCollapsedList<T>(items: T[], limit = 8) {
  const [all, setAll] = useState(false);
  return {
    visible: all ? items : items.slice(0, limit),
    hidden: Math.max(0, items.length - limit),
    all,
    toggle: () => setAll((v) => !v),
  };
}

export function ShowAllButton({ hidden, all, onToggle, noun = 'people' }: { hidden: number; all: boolean; onToggle: () => void; noun?: string }) {
  if (hidden <= 0) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-2 text-body-sm font-semibold text-primary transition hover:border-primary/40 hover:bg-primary-container/40"
    >
      {all ? 'Show fewer' : `Show all · ${hidden} more ${noun}`}
      <ChevronDown className={`h-4 w-4 transition ${all ? 'rotate-180' : ''}`} aria-hidden />
    </button>
  );
}
